// React and hooks
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ChangeEvent,
  memo,
} from 'react'

// Redux-related imports
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { RootState, AppDispatch } from '@store/index'
import { useAiReceptionistActorId } from '@hooks/useAiReceptionistActorId'
import {
  AI_APPOINTMENT_FILTER_OPTIONS,
  matchesAiReceptionistRecordFilter,
} from '@utils/aiReceptionist.util'

// Third-party libraries
import { Row, Col, Tab, Container } from 'react-bootstrap'
import moment from 'moment'

// Custom components
import {
  DayCalendar,
  MonthCalendar,
  WeekCalendar,
} from '@components/pages/Appointments/index'
import Button from '@components/common/Button/Button.component'
import SearchWithFilter, {
  FilterSelection,
} from '@components/common/SearchWithFilter/SearchWithFilter.component'
import { ScheduleAppointment } from '@components/layout/Modals'
import CalendarToggle from '@components/pages/Appointments/CalendarToggle.component'
import AddPatientModal from '@components/layout/Modals/AddPatientModal/AddPatientModal.component'

// Constants and utilities
import { NAV_ITEMS, APPT_STATUS_MENU_ITEMS } from '@constants/AppointmentPage'

// Redux actions and state management
import {
  addAppointment,
  fetchAllAppointments,
  updateAppointment,
} from '@store/actions/appointment.action'

// Types and interfaces
import {
  AppointmentRequest,
  AppointmentResponse,
  PatientWithInsurance,
  AppointmentStatus,
  IAppointment,
} from '@dataTypes/appointment.type'
import { INote, NoteContextType } from '@dataTypes/note.type'
import { Patient } from '@dataTypes/PatientInfo.types'

// Relative imports
import { addPatient } from '@store/actions/patientmanagement.action'
import {
  fetchAllAppointmentTypes,
  fetchAllProcedures,
  fetchAllergiesCatalog,
  fetchMedicalConditionsCatalog,
} from '@store/actions'
import { calculateDateRange, formatDateTime } from '@utils/time.util'
import '@components/pages/Appointments/Scheduling.style.css'
import AppointmentSkeleton from './AppointmentSkeleton'
import ButtonGroup from '@components/common/ButtonGroup/ButtonGroup.component'
import { useAppSelector } from '@store/hooks'
import LegendInfoModal from '@components/layout/Modals/Appointment/LegendInfo.modal'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  useClinicOperatories,
  useClinicProviders,
  useGlobalLoadingActions,
} from '@hooks'
import {
  useNoteEditorOverlay,
  type NoteMutationSuccessDetail,
  getBackButtonLabelForOrigin,
} from '@components/common/NoteEditorOverlay'
import { resolveAppointmentNoteOverlayPayload } from './appointmentNoteOverlay.util'
import type { AppointmentNoteOverlayInput } from './appointmentNoteOverlay.util'
import { fetchPatientById } from '@services/api/patient.api'
import { getAllNotes } from '@services/api/note.api'
import { useNotificationToast } from '@context/NotificationToastContext'
import { createFailedToast } from '@utils/notificationToast.util'
import { getSchedulerSettings } from '@services/api/appointment.api'
import {
  INITIAL_SCHEDULER_SETTINGS_DATA,
  SchedulerSettingsType,
} from '@components/pages/Settings/System/Scheduler/Scheduler.component'
import { providerRoles, firstAppointedProviderId } from '@utils/provider.util'
import { fetchClinicOpeningHoursApi } from '@services/api/clinic-opening-hours.api'
import { fetchClinicOverridesApi } from '@services/api/clinic-overrides.api'
import { fetchClinicHolidaysApi } from '@services/api/clinic-holidays.api'
import { IClinicOpeningHoursRow } from '@dataTypes/clinic-opening-hours.type'
import { IClinicHolidayRow } from '@dataTypes/clinic-holidays.type'
import { IClinicOverrideRow } from '@dataTypes/clinic-overrides.type'
import {
  DateScheduleResult,
  getScheduleForDateRange,
} from '@components/pages/Appointments/DayCalendar/DayCalendarTemplate'
import {
  Scheduler,
  type SchedulerEvent,
  type SchedulerSlotSelection,
  type SchedulerView,
} from '@components/ui'

import {
  isDsSchedulerEnabled,
  resolveSchedulerData,
  resolveSchedulerViewMode,
} from './mapAppointmentToScheduler'
import { logger } from '@utils/safeLogger'
export type IAppointmentDetails = {
  startTime: Date | string
  endTime: Date | string
  room: string
  provider: string
}

const SEARCH_DEBOUNCE_MS = 300
const APPOINTMENT_NOTE_OVERLAY_BODY_CLASS = 'note-overlay-from-appointment'

const getDefaultTab = (pageState: { calendarView?: string } | null): string => {
  if (pageState?.calendarView) {
    switch (pageState.calendarView.toLowerCase()) {
      case 'day':
        return 'day'
      case 'workweek':
        return 'week'
      case 'month':
        return 'month'
      default:
        return 'day'
    }
  }
  return localStorage.getItem('default_calendar_view') || 'day'
}

const getDefaultDate = (pageState: { startDate?: string } | null): string => {
  if (pageState?.startDate) {
    return moment(pageState.startDate).format('MMMM D, YYYY')
  }
  return moment().format('MMMM D, YYYY')
}

const statusLabelMap: Record<AppointmentStatus, string> = {
  [AppointmentStatus.Confirmed]:
    APPT_STATUS_MENU_ITEMS[AppointmentStatus.Confirmed].text,
  [AppointmentStatus.Unconfirmed]:
    APPT_STATUS_MENU_ITEMS[AppointmentStatus.Unconfirmed].text,
  [AppointmentStatus.Late]: APPT_STATUS_MENU_ITEMS[AppointmentStatus.Late].text,
  [AppointmentStatus.NoShow]:
    APPT_STATUS_MENU_ITEMS[AppointmentStatus.NoShow].text,
  [AppointmentStatus.CheckIn]:
    APPT_STATUS_MENU_ITEMS[AppointmentStatus.CheckIn].text,
  [AppointmentStatus.InTreatment]:
    APPT_STATUS_MENU_ITEMS[AppointmentStatus.InTreatment].text,
  [AppointmentStatus.CheckOut]:
    APPT_STATUS_MENU_ITEMS[AppointmentStatus.CheckOut].text,
  [AppointmentStatus.Completed]:
    APPT_STATUS_MENU_ITEMS[AppointmentStatus.Completed].text,
  [AppointmentStatus.Pending]: 'Pending',
  [AppointmentStatus.Alert]: 'Alert',
  [AppointmentStatus.FollowUp]: 'Follow-up',
  [AppointmentStatus.NewPatient]: 'New Patient',
  [AppointmentStatus.Cancelled]: 'Cancelled',
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs)
    return () => window.clearTimeout(timer)
  }, [value, delayMs])
  return debounced
}

type AppointmentCalendarViewProps = {
  activeTab: string
  date: string
  appointmentList: AppointmentResponse[]
  providerList: any[]
  filteredOperatories: any[]
  isHIPAAView: boolean
  schedulerSettings: ExtendedSchedulerSettingsType
  startDate: string
  endDate: string
  onViewLoadedDayWeek: () => void
  onViewLoadedMonth: () => void
  toggleApptModal: (isOpen: boolean, appt?: AppointmentResponse | null) => void
  setAppointmentDetails: React.Dispatch<
    React.SetStateAction<IAppointmentDetails>
  >
  onViewNote: (input: AppointmentNoteOverlayInput) => void
  aiActorId: string | null
}

const AppointmentCalendarView = memo(function AppointmentCalendarView({
  activeTab,
  date,
  appointmentList,
  providerList,
  filteredOperatories,
  isHIPAAView,
  schedulerSettings,
  onViewLoadedDayWeek,
  onViewLoadedMonth,
  toggleApptModal,
  setAppointmentDetails,
  onViewNote,
  aiActorId,
}: AppointmentCalendarViewProps): React.ReactElement {
  return (
    <Tab.Content>
      <Tab.Pane eventKey="day" mountOnEnter>
        {activeTab === 'day' && (
          <DayCalendar
            selectDate={date}
            appointmentList={appointmentList}
            providerList={providerList}
            operatoryList={filteredOperatories}
            isHipaa={isHIPAAView}
            apptModalToggle={toggleApptModal}
            setAppointmentDetails={setAppointmentDetails}
            onViewLoaded={onViewLoadedDayWeek}
            onViewNote={onViewNote}
            schedulerSettings={schedulerSettings}
            aiActorId={aiActorId}
          />
        )}
      </Tab.Pane>
      <Tab.Pane eventKey="week" mountOnEnter>
        {activeTab === 'week' && (
          <WeekCalendar
            selectDate={date}
            appointmentList={appointmentList}
            providerList={providerList}
            operatoryList={filteredOperatories}
            isHipaa={isHIPAAView}
            apptModalToggle={toggleApptModal}
            setAppointmentDetails={setAppointmentDetails}
            onViewLoaded={onViewLoadedDayWeek}
            onViewNote={onViewNote}
            schedulerSettings={schedulerSettings}
            aiActorId={aiActorId}
          />
        )}
      </Tab.Pane>
      <Tab.Pane eventKey="month" mountOnEnter>
        {activeTab === 'month' && (
          <MonthCalendar
            selectDate={date}
            appointmentList={appointmentList as unknown as IAppointment[]}
            onViewLoaded={onViewLoadedMonth}
            schedulerSettings={schedulerSettings}
          />
        )}
      </Tab.Pane>
    </Tab.Content>
  )
})

export type ExtendedSchedulerSettingsType = SchedulerSettingsType & {
  opening_hours?: IClinicOpeningHoursRow[]
  holidays?: IClinicHolidayRow[]
  overrides?: IClinicOverrideRow[]
  dateSchedule?: DateScheduleResult[]
}

function AppointmentPage(): React.ReactElement {
  useClinicOperatories()
  useClinicProviders()

  const location = useLocation()
  const navigate = useNavigate()
  // Stabilise so downstream effects don't re-fire on every render due to a
  // fresh `{}` fallback each time.
  const pageState = useMemo(() => location.state || {}, [location.state])
  // Ref guard so an `appointmentId` carried in route state (e.g. from a
  // notification action) only auto-opens the modal once per navigation; we
  // clear `location.state` after opening so refreshes or back-navigation
  // don't re-trigger it.
  const autoOpenedApptIdRef = useRef<string | null>(null)
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const aiActorId = useAiReceptionistActorId()
  const isHIPAAView = useAppSelector((state) => state.ui.isHIPAAView)
  const { selectedPatient, refresh } = useAppSelector(
    (state) => state.patient,
    shallowEqual,
  )

  const apptList = useSelector(
    (state: RootState) => state.appointment.data,
    shallowEqual,
  )
  const apptFetchError = useSelector(
    (state: RootState) => state.appointment.error.fetch,
  )
  const useDsScheduler = useMemo(
    () => isDsSchedulerEnabled(location.search),
    [location.search],
  )
  const providerList = useSelector(
    (state: RootState) =>
      state.provider.data.filter((provider) =>
        providerRoles.includes(provider.work_role ?? ''),
      ),
    shallowEqual,
  )
  const operatoryList = useSelector(
    (state: RootState) => state.operatory.data,
    shallowEqual,
  )

  const dispatch = useDispatch<AppDispatch>()
  const { showToast } = useNotificationToast()
  const { hideLoading } = useGlobalLoadingActions()
  const { openNoteEditor, closeNoteEditor } = useNoteEditorOverlay()

  const [activeTab, setActiveTab] = useState<string>(() =>
    getDefaultTab(pageState),
  )
  const [selectedProviders, setSelectedProviders] = useState<string[]>([])
  const [selectedOperatories, setSelectedOperatories] = useState<string[]>([])
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([])
  const [selectedAiActivity, setSelectedAiActivity] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState<string>('')
  const debouncedSearchQuery = useDebouncedValue(
    searchQuery,
    SEARCH_DEBOUNCE_MS,
  )
  const [filterResetKey, setFilterResetKey] = useState<number>(0)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [showAddPatientModal, setShowAddPatientModal] = useState(false)
  const [isLegendInfoModalOpen, setIsLegendInfoModalOpen] =
    useState<boolean>(false)
  const [date, setDate] = useState(() => getDefaultDate(pageState))
  const [currentAppointment, setCurrentAppointment] =
    useState<AppointmentResponse | null>(null)
  const [appointmentDetails, setAppointmentDetails] =
    useState<IAppointmentDetails>({
      startTime: '',
      endTime: '',
      room: '',
      provider: '',
    })
  const [schedulerSettings, setSchedulerSettings] =
    useState<ExtendedSchedulerSettingsType>(INITIAL_SCHEDULER_SETTINGS_DATA)

  const apptNoteOverlayContextRef = useRef<{
    appointmentId: string
    patientId: string
  } | null>(null)
  const apptNotesSyncRef = useRef<
    ((notes: Partial<INote>[]) => void) | undefined
  >(undefined)
  const apptNoteSavedRef = useRef<((note: INote) => void) | undefined>(
    undefined,
  )

  const clinicId = selectedClinic?.clinic_id

  const { startDate, endDate } = useMemo(
    () => calculateDateRange(activeTab, date),
    [activeTab, date],
  )

  const rangeKey = useMemo(() => {
    if (!clinicId || !startDate || !endDate) return ''
    return `${clinicId}|${startDate}|${endDate}`
  }, [clinicId, startDate, endDate])

  const dateScheduleForRange = useMemo(
    () =>
      getScheduleForDateRange(
        startDate,
        endDate,
        schedulerSettings,
        schedulerSettings.opening_hours ?? [],
        schedulerSettings.holidays ?? [],
        schedulerSettings.overrides ?? [],
      ),
    [startDate, endDate, schedulerSettings],
  )

  const calendarSchedulerSettings = useMemo(
    (): ExtendedSchedulerSettingsType => ({
      ...schedulerSettings,
      dateSchedule: dateScheduleForRange,
    }),
    [schedulerSettings, dateScheduleForRange],
  )

  const [loadedRangeKey, setLoadedRangeKey] = useState<string | null>(null)
  const [settledRangeKey, setSettledRangeKey] = useState<string | null>(null)
  const [viewReadyKey, setViewReadyKey] = useState<string | null>(null)
  const [schedulerSettingsReady, setSchedulerSettingsReady] =
    useState<boolean>(false)
  const activeRangeKeyRef = useRef(rangeKey)
  activeRangeKeyRef.current = rangeKey

  const viewKey = rangeKey ? `${rangeKey}|${activeTab}` : ''
  const viewKeyRef = useRef(viewKey)
  viewKeyRef.current = viewKey

  const isDataPending = !rangeKey || settledRangeKey !== rangeKey
  const canMountCalendar = !isDataPending && schedulerSettingsReady
  const isViewPending = canMountCalendar && viewReadyKey !== viewKey
  const showCalendarSkeleton =
    isDataPending || !schedulerSettingsReady || isViewPending

  const appointmentsForView = useMemo((): AppointmentResponse[] => {
    if (loadedRangeKey === rangeKey) {
      return apptList ?? []
    }
    return []
  }, [apptList, rangeKey, loadedRangeKey])

  const operatoryNameById = useMemo(() => {
    const map = new Map<string, string>()
    operatoryList.forEach((o) => map.set(o.operatory_id, o.name.toLowerCase()))
    return map
  }, [operatoryList])

  const createBlockEvent = (
    id: string,
    startDateTime: string,
    endDateTime: string,
    operatorId?: string,
  ): any => ({
    Id: id,
    appointment_start_date: startDateTime,
    appointment_end_date: endDateTime,
    ...(operatorId ? { operatory_id: operatorId } : {}),
    IsBlock: true,
  })

  const adjustBlockDateTime = (
    time: string | undefined,
  ): string | undefined => {
    if (!time) return time
    return moment(time, 'HH:mm:ss').subtract(1, 'second').format('HH:mm:ss')
  }

  const addBlockEvents = (dateSchedules: DateScheduleResult[]): any[] => {
    if (activeTab === 'month') return []

    const blockEvents: any[] = []

    for (const dateSchedule of dateSchedules ?? []) {
      const dateToBlockString = dateSchedule.date
      if (activeTab === 'day') {
        blockEvents.push(
          ...(operatoryList.length > 0
            ? operatoryList.flatMap((operator) => [
                ...(dateSchedule.schedule.opening_from !== '00:00:00'
                  ? [
                      createBlockEvent(
                        `${operator.operatory_id}-block-${dateToBlockString}-before`,
                        `${dateToBlockString} 00:00:00`,
                        dateSchedule.schedule.opening_from
                          ? `${dateToBlockString} ${adjustBlockDateTime(dateSchedule.schedule.opening_from) ?? '11:59:59'}`
                          : `${dateToBlockString} 11:59:59`,
                        operator.operatory_id,
                      ),
                    ]
                  : []),
                ...(dateSchedule.schedule.opening_to !== '24:00:00'
                  ? [
                      createBlockEvent(
                        `${operator.operatory_id}-block-${dateToBlockString}-after`,
                        dateSchedule.schedule.opening_to
                          ? `${dateToBlockString} ${adjustBlockDateTime(dateSchedule.schedule.opening_to) ?? '12:00:00'}`
                          : `${dateToBlockString} 12:00:00`,
                        `${dateToBlockString} 23:59:59`,
                        operator.operatory_id,
                      ),
                    ]
                  : []),
              ])
            : []),
        )
      } else {
        blockEvents.push(
          createBlockEvent(
            `block-${dateToBlockString}-before`,
            `${dateToBlockString} 00:00:00`,
            dateSchedule.schedule.opening_from
              ? `${dateToBlockString} ${adjustBlockDateTime(dateSchedule.schedule.opening_from) ?? '11:59:59'}`
              : `${dateToBlockString} 11:59:59`,
          ),
          createBlockEvent(
            `block-${dateToBlockString}-after`,
            dateSchedule.schedule.opening_to
              ? `${dateToBlockString} ${adjustBlockDateTime(dateSchedule.schedule.opening_to) ?? '12:00:00'}`
              : `${dateToBlockString} 12:00:00`,
            `${dateToBlockString} 23:59:59`,
          ),
        )
      }
    }

    return blockEvents
  }

  const filteredAppointments = useMemo(() => {
    const blockEvents = addBlockEvents(dateScheduleForRange)
    if (!appointmentsForView?.length) return [...blockEvents]

    let filtered = appointmentsForView

    const searchLower = debouncedSearchQuery.trim().toLowerCase()
    if (searchLower) {
      filtered = filtered.filter((appt) => {
        const patientName =
          `${appt.patient?.first_name || ''} ${appt.patient?.last_name || ''}`.toLowerCase()
        const providerName =
          `${appt.provider?.first_name || ''} ${appt.provider?.last_name || ''}`.toLowerCase()
        const appointmentType = appt.appointmenttype?.name?.toLowerCase() || ''
        const operatoryName = operatoryNameById.get(appt.operatory_id) || ''

        return (
          patientName.includes(searchLower) ||
          providerName.includes(searchLower) ||
          appointmentType.includes(searchLower) ||
          operatoryName.includes(searchLower)
        )
      })
    }

    if (
      selectedProviders.length > 0 &&
      selectedProviders.length < providerList.length
    ) {
      filtered = filtered.filter((appt) =>
        selectedProviders.includes(appt.provider_id),
      )
    }

    if (
      selectedOperatories.length > 0 &&
      selectedOperatories.length < operatoryList.length
    ) {
      filtered = filtered.filter((appt) =>
        selectedOperatories.includes(appt.operatory_id),
      )
    }

    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((appt) => {
        const statusLabel =
          statusLabelMap[appt.appt_status as AppointmentStatus]
        return statusLabel && selectedStatuses.includes(statusLabel)
      })
    }

    filtered = filtered.filter((appt) =>
      matchesAiReceptionistRecordFilter(appt, aiActorId, selectedAiActivity),
    )

    return [...blockEvents, ...filtered]
  }, [
    appointmentsForView,
    dateScheduleForRange,
    activeTab,
    operatoryList,
    debouncedSearchQuery,
    selectedProviders,
    selectedOperatories,
    selectedStatuses,
    selectedAiActivity,
    aiActorId,
    providerList.length,
    operatoryList.length,
    operatoryNameById,
  ])

  const calendarAppointments = useMemo(
    () => filteredAppointments,
    [filteredAppointments],
  )

  const filteredOperatories = useMemo(() => {
    if (!selectedOperatories.length) return operatoryList
    if (selectedOperatories.length === operatoryList.length) {
      return operatoryList
    }
    return operatoryList.filter((operatory) =>
      selectedOperatories.includes(operatory.operatory_id),
    )
  }, [operatoryList, selectedOperatories])

  const schedulerDate = useMemo(
    () => moment(date, 'MMMM D, YYYY').toDate(),
    [date],
  )

  const dsSchedulerData = useMemo(() => {
    if (!useDsScheduler) return null
    return resolveSchedulerData({
      appointments: calendarAppointments,
      operatories: filteredOperatories,
      providers: providerList,
      date: schedulerDate,
      isHipaa: isHIPAAView,
      clinicName: selectedClinic?.clinic_name,
      fetchError: apptFetchError,
      aiUserId: aiActorId,
    })
  }, [
    useDsScheduler,
    calendarAppointments,
    filteredOperatories,
    providerList,
    schedulerDate,
    isHIPAAView,
    selectedClinic?.clinic_name,
    apptFetchError,
    aiActorId,
  ])

  const [dsEvents, setDsEvents] = useState<SchedulerEvent[]>([])

  useEffect(() => {
    if (!dsSchedulerData) return
    setDsEvents(dsSchedulerData.events)
  }, [dsSchedulerData])

  const fetchSchedulerSettings = useCallback(async (): Promise<void> => {
    const clinicOpeningHours = {
      opening_hours: [] as IClinicOpeningHoursRow[],
      holidays: [] as IClinicHolidayRow[],
      overrides: [] as IClinicOverrideRow[],
    }
    try {
      const schedulerClinicId =
        clinicId ?? localStorage.getItem('clinic_id') ?? ''
      const [
        openingHoursResponse,
        overridesResponse,
        holidaysResponse,
        schedulerSettingsResponse,
      ] = await Promise.all([
        fetchClinicOpeningHoursApi(schedulerClinicId),
        fetchClinicOverridesApi(schedulerClinicId),
        fetchClinicHolidaysApi(schedulerClinicId),
        getSchedulerSettings(),
      ])
      if (
        openingHoursResponse.statusCode === 200 &&
        openingHoursResponse.data?.opening_hours
      ) {
        clinicOpeningHours.opening_hours =
          openingHoursResponse.data.opening_hours.schedule
      }
      if (
        overridesResponse.statusCode === 200 &&
        overridesResponse.data?.clinic_overrides
      ) {
        clinicOpeningHours.overrides =
          overridesResponse.data.clinic_overrides.overrides
      }
      if (
        holidaysResponse.statusCode === 200 &&
        holidaysResponse.data?.clinic_holidays
      ) {
        clinicOpeningHours.holidays =
          holidaysResponse.data.clinic_holidays.holidays
      }
      if (
        schedulerSettingsResponse.statusCode === 200 &&
        'scheduler_settings' in schedulerSettingsResponse.data
      ) {
        const schedulerSettingsData =
          schedulerSettingsResponse.data.scheduler_settings ||
          INITIAL_SCHEDULER_SETTINGS_DATA
        setSchedulerSettings((prev) => ({
          ...prev,
          ...schedulerSettingsData,
          ...clinicOpeningHours,
        }))
        const defaultCalendarView =
          schedulerSettingsData.default_calendar_view.toLowerCase()
        if (
          defaultCalendarView !== localStorage.getItem('default_calendar_view')
        ) {
          localStorage.setItem('default_calendar_view', defaultCalendarView)
          setActiveTab(defaultCalendarView)
        }
      }
    } catch (error) {
      console.error(error)
    }
  }, [clinicId, selectedClinic])

  useEffect(() => {
    let cancelled = false
    setSchedulerSettingsReady(false)
    void (async (): Promise<void> => {
      await fetchSchedulerSettings()
      if (!cancelled) {
        setSchedulerSettingsReady(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [fetchSchedulerSettings, selectedClinic])

  const apptNotesBackLabel = useMemo(
    () => getBackButtonLabelForOrigin({ path: '/appointment' }),
    [],
  )

  const addAppointmentOverlayBodyClass = useCallback(() => {
    document?.body?.classList?.add(APPOINTMENT_NOTE_OVERLAY_BODY_CLASS)
  }, [])

  const removeAppointmentOverlayBodyClass = useCallback(() => {
    document?.body?.classList?.remove(APPOINTMENT_NOTE_OVERLAY_BODY_CLASS)
  }, [])

  useEffect(() => {
    return () => {
      removeAppointmentOverlayBodyClass()
    }
  }, [removeAppointmentOverlayBodyClass])

  const handleApptNoteMutationSuccess = useCallback(
    (detail?: NoteMutationSuccessDetail): void => {
      const ctx = apptNoteOverlayContextRef.current
      const syncNotes = apptNotesSyncRef.current
      if (ctx == null || syncNotes == null) return
      const { patientId, appointmentId } = ctx

      const matchesAppointmentContext = (n: INote): boolean =>
        String(n.context_type ?? '').toLowerCase() ===
          NoteContextType.APPOINTMENT &&
        n.context_id != null &&
        String(n.context_id) === String(appointmentId)

      const readScoped = async (): Promise<INote[]> => {
        const scoped = await getAllNotes({
          patientId,
          contextType: NoteContextType.APPOINTMENT,
          contextId: appointmentId,
        })
        if (scoped.statusCode !== 200) return []
        return scoped.data?.notes ?? []
      }

      const readFilteredFallback = async (): Promise<INote[]> => {
        const allPatient = await getAllNotes({ patientId })
        if (allPatient.statusCode !== 200) return []
        return (allPatient.data?.notes ?? []).filter(matchesAppointmentContext)
      }

      const delay = (ms: number): Promise<void> =>
        new Promise((resolve) => {
          setTimeout(resolve, ms)
        })

      void (async (): Promise<void> => {
        let list = await readScoped()
        if (list.length === 0) {
          list = await readFilteredFallback()
        }

        const saved = detail?.savedNote
        let needSavedId: string | undefined
        if (saved != null && matchesAppointmentContext(saved)) {
          needSavedId = saved.note_id
        }

        if (
          needSavedId != null &&
          !list.some((n) => n.note_id === needSavedId)
        ) {
          for (let attempt = 0; attempt < 2; attempt += 1) {
            await delay(400)
            list = await readScoped()
            if (list.length === 0) {
              list = await readFilteredFallback()
            }
            if (list.some((n) => n.note_id === needSavedId)) break
          }
        }

        if (saved != null && matchesAppointmentContext(saved)) {
          if (!list.some((n) => n.note_id === saved.note_id)) {
            list = [...list, saved]
          }
        }

        if (
          list.length === 0 &&
          saved != null &&
          matchesAppointmentContext(saved)
        ) {
          list = [saved]
        }

        syncNotes(list)
      })()
    },
    [],
  )

  const handleOpenApptNotesOverlay = useCallback(
    (input: AppointmentNoteOverlayInput): void => {
      const { patientId, appointmentId, contextName, noteId, openInEditMode } =
        input
      const normalizedNoteId = noteId?.trim() || null
      const shouldOpenFirstNote =
        openInEditMode !== true && normalizedNoteId !== null

      if (!patientId) {
        showToast(
          createFailedToast(
            'Notes',
            'No patient associated with this appointment',
          ),
        )
        return
      }

      const payload = resolveAppointmentNoteOverlayPayload({
        appointmentId,
        patientId,
        contextName,
        openFirstNote: shouldOpenFirstNote,
      })

      apptNoteOverlayContextRef.current = { appointmentId, patientId }
      apptNotesSyncRef.current = input.onNotesSync
      apptNoteSavedRef.current = input.onNoteSaved

      void fetchPatientById(patientId)
        .then((response) => {
          if (response.statusCode !== 200 || !response.data?.patient) {
            throw new Error(response.message || 'Failed to load patient data')
          }
          addAppointmentOverlayBodyClass()
          openNoteEditor({
            patient: response.data.patient as unknown as Patient,
            contextId: payload.contextId ?? null,
            contextType: payload.contextType ?? null,
            contextName: payload.contextName ?? null,
            openFirstNote: payload.openFirstNote ?? false,
            openNoteId: normalizedNoteId,
            openNoteInEditMode: openInEditMode === true,
            initialView: normalizedNoteId ? 'list' : 'add',
            editorBackButtonLabel: apptNotesBackLabel,
            overlayBackLabel: apptNotesBackLabel,
            openContextNotesByDefault: true,
            anchorClassName: 'appt-note-editor-overlay-anchor',
            portalTarget: '.page-content',
            notesFetchLoadingContainer: 'body',
            onNoteMutationSuccess: handleApptNoteMutationSuccess,
            onNoteSavedImmediate: (note) => {
              apptNoteSavedRef.current?.(note)
            },
            onOverlayWillClose: () => {
              removeAppointmentOverlayBodyClass()
              apptNotesSyncRef.current = undefined
              apptNoteOverlayContextRef.current = null
              apptNoteSavedRef.current = undefined
            },
          })
        })
        .catch(() => {
          removeAppointmentOverlayBodyClass()
          showToast(createFailedToast('Notes', 'Failed to load patient data'))
          closeNoteEditor()
        })
    },
    [
      showToast,
      apptNotesBackLabel,
      openNoteEditor,
      closeNoteEditor,
      addAppointmentOverlayBodyClass,
      removeAppointmentOverlayBodyClass,
      handleApptNoteMutationSuccess,
    ],
  )

  const providerNameToIdMap = useMemo(() => {
    const map = new Map<string, string>()
    providerList.forEach((provider) => {
      const name = `Dr. ${provider.first_name} ${provider.last_name}`
      map.set(name, provider.provider_id)
    })
    return map
  }, [providerList])

  const operatoryNameToIdMap = useMemo(() => {
    const map = new Map<string, string>()
    operatoryList.forEach((operatory) => {
      map.set(operatory.name, operatory.operatory_id)
    })
    return map
  }, [operatoryList])

  const filterOptions = useMemo(() => {
    const statusOptions = Object.entries(APPT_STATUS_MENU_ITEMS)
      .map(([status, item]) => ({
        status: Number(status) as AppointmentStatus,
        label: item.text,
        position: item.position,
      }))
      .sort((a, b) => a.position - b.position)
      .map((item) => item.label)

    return {
      Providers: providerList.map((p) => `Dr. ${p.first_name} ${p.last_name}`),
      Operatories: operatoryList.map((o) => o.name),
      Status: statusOptions,
      aiReceptionist: AI_APPOINTMENT_FILTER_OPTIONS,
    }
  }, [providerList, operatoryList])

  const handleFilterChange = useCallback(
    (filters: FilterSelection): void => {
      const providerIds = (filters.Providers || [])
        .map((name) => providerNameToIdMap.get(name))
        .filter((id): id is string => id !== undefined)

      const operatoryIds = (filters.Operatories || [])
        .map((name) => operatoryNameToIdMap.get(name))
        .filter((id): id is string => id !== undefined)

      setSelectedProviders(providerIds)
      setSelectedOperatories(operatoryIds)
      setSelectedStatuses(filters.Status || [])
      setSelectedAiActivity(filters.aiReceptionist || [])
    },
    [providerNameToIdMap, operatoryNameToIdMap],
  )

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      setSearchQuery(event.target.value)
    },
    [],
  )

  const handleResetFilters = useCallback((): void => {
    setSearchQuery('')
    setSelectedProviders(providerList.map((p) => p.provider_id))
    setSelectedOperatories(operatoryList.map((o) => o.operatory_id))
    setSelectedStatuses([])
    setSelectedAiActivity([])
    setFilterResetKey((prev) => prev + 1)
  }, [providerList, operatoryList])

  useEffect(() => {
    if (
      providerList.length > 0 &&
      operatoryList.length > 0 &&
      selectedProviders.length === 0 &&
      selectedOperatories.length === 0
    ) {
      setSelectedProviders(providerList.map((p) => p.provider_id))
      setSelectedOperatories(operatoryList.map((o) => o.operatory_id))
    }
  }, [
    providerList,
    operatoryList,
    selectedProviders.length,
    selectedOperatories.length,
  ])

  useEffect(() => {
    if (!providerList.length || !operatoryList.length) return
    setSelectedProviders(providerList.map((p) => p.provider_id))
    setSelectedOperatories(operatoryList.map((o) => o.operatory_id))
  }, [selectedClinic?.clinic_id, providerList, operatoryList])

  const toggleApptModal = (
    isOpen: boolean,
    appt: AppointmentResponse | null = null,
  ): void => {
    if (!isOpen) {
      removeAppointmentOverlayBodyClass()
      apptNotesSyncRef.current = undefined
      apptNoteOverlayContextRef.current = null
      apptNoteSavedRef.current = undefined
    }
    setIsModalOpen(isOpen)
    setCurrentAppointment(appt)
  }

  const openSetAppointmentFromSlot = useCallback(
    (slot: SchedulerSlotSelection): void => {
      const dateFormat = 'YYYY-MM-DD HH:mm:ss'
      const knownRoom =
        activeTab === 'day' &&
        operatoryList.some(
          (operatory) => operatory.operatory_id === slot.columnId,
        )
          ? slot.columnId
          : ''
      const roomId =
        knownRoom ||
        filteredOperatories[0]?.operatory_id ||
        operatoryList[0]?.operatory_id ||
        ''
      const room = operatoryList.find(
        (operatory) => operatory.operatory_id === roomId,
      )
      const appointedProviderId = firstAppointedProviderId(
        room?.operatory_providers,
        providerList.map((provider) => provider.provider_id),
      )
      setAppointmentDetails({
        startTime: formatDateTime(slot.start, dateFormat),
        endTime: formatDateTime(slot.end, dateFormat),
        room: roomId,
        provider: appointedProviderId ?? '',
      })
      setCurrentAppointment(null)
      setIsModalOpen(true)
    },
    [activeTab, filteredOperatories, operatoryList, providerList],
  )

  const handleDsEventClick = useCallback(
    (event: SchedulerEvent): void => {
      const live = calendarAppointments.find(
        (appt) =>
          appt.appointment_id === event.id &&
          !(appt as AppointmentResponse & { IsBlock?: boolean }).IsBlock,
      )
      if (live) {
        setCurrentAppointment(live)
        setIsModalOpen(true)
        return
      }
      openSetAppointmentFromSlot({
        start: event.start,
        end: event.end,
        columnId: event.columnId,
      })
    },
    [calendarAppointments, openSetAppointmentFromSlot],
  )

  // Notification deep-link: when something navigates here with an
  // `appointmentId` in route state (notification toast or Notifications page
  // action), auto-open the appointment modal once the appointment list has
  // loaded. We then strip the state so the modal doesn't re-open after the
  // user closes it. Inlined the open-modal call (rather than depending on
  // `toggleApptModal`) so the effect's dependency array stays stable.
  useEffect(() => {
    const targetId = pageState?.appointmentId as string | undefined
    if (!targetId) return
    if (autoOpenedApptIdRef.current === targetId) return
    if (!apptList || apptList.length === 0) return

    const target = apptList.find(
      (a: AppointmentResponse) => a.appointment_id === targetId,
    )
    if (!target) return

    autoOpenedApptIdRef.current = targetId
    setIsModalOpen(true)
    setCurrentAppointment(target)
    navigate(location.pathname, { replace: true, state: null })
  }, [apptList, pageState, navigate, location.pathname])

  const handleTabChange = useCallback(
    (eventKey: string): void => {
      if (activeTab === eventKey) return
      setActiveTab(eventKey)
    },
    [activeTab],
  )

  const shiftDate = useCallback(
    (direction: 'prev' | 'next'): void => {
      setDate((current) => {
        const unit =
          activeTab === 'week'
            ? 'weeks'
            : activeTab === 'month'
              ? 'months'
              : 'days'
        const next = moment(current, 'MMMM D, YYYY')
        return direction === 'prev'
          ? next.subtract(1, unit).format('MMMM D, YYYY')
          : next.add(1, unit).format('MMMM D, YYYY')
      })
    },
    [activeTab],
  )

  const handlePrevDay = useCallback((): void => {
    shiftDate('prev')
  }, [shiftDate])

  const handleNextDay = useCallback((): void => {
    shiftDate('next')
  }, [shiftDate])

  type FetchAppointmentsResult = 'committed' | 'stale' | 'failed'

  const fetchAppointments =
    useCallback(async (): Promise<FetchAppointmentsResult> => {
      if (!startDate || !endDate || !rangeKey) return 'failed'
      try {
        await dispatch(fetchAllAppointments({ startDate, endDate })).unwrap()
        if (activeRangeKeyRef.current === rangeKey) {
          setLoadedRangeKey(rangeKey)
          setSettledRangeKey(rangeKey)
          return 'committed'
        }
        return 'stale'
      } catch {
        if (activeRangeKeyRef.current === rangeKey) {
          setSettledRangeKey(rangeKey)
        }
        return 'failed'
      }
    }, [dispatch, startDate, endDate, rangeKey])

  const handleOnSave = useCallback(
    async (apptDetails: Partial<AppointmentRequest>): Promise<void> => {
      try {
        if (apptDetails?.appointment_id) {
          await dispatch(
            updateAppointment({
              appointment_id: apptDetails.appointment_id,
              updates: apptDetails,
            }),
          ).unwrap()
          await fetchAppointments()
        } else {
          await dispatch(
            addAppointment({
              appointmentData: apptDetails,
              date,
              activeTab,
            }),
          ).unwrap()
        }
      } catch (error) {
        logger.error('Failed to save appointment:', error)
      }
    },
    [dispatch, fetchAppointments, date, activeTab],
  )

  const handleDateChange = useCallback((dateString: string): void => {
    setDate((current) => {
      if (moment(dateString).isSame(moment(current, 'MMMM D, YYYY'), 'day')) {
        return current
      }
      return dateString
    })
  }, [])

  const toggleAddPatientModal = useCallback((): void => {
    setShowAddPatientModal((open) => !open)
  }, [])

  const cleanEmptyNullableFields = <T extends Record<string, unknown>>(
    data: T,
  ): Partial<T> => {
    const cleaned: Partial<T> = { ...data }
    for (const key in cleaned) {
      if (cleaned[key] === '') {
        delete cleaned[key]
      }
    }
    return cleaned
  }

  const handleSavePatient = useCallback(
    (newPatientData: Patient): void => {
      dispatch(addPatient(cleanEmptyNullableFields(newPatientData)))
      setShowAddPatientModal(false)
    },
    [dispatch],
  )

  const applyScrollPosition = useCallback((): void => {
    if (pageState?.scrollTop == null || pageState?.scrollLeft == null) return
    const contentElement = document.querySelector('.e-content-wrap')
    if (!contentElement) return
    contentElement.scrollTop = pageState.scrollTop
    contentElement.scrollLeft = pageState.scrollLeft
    location.state = null
    navigate(location.pathname, { replace: true, state: {} })
  }, [location, navigate, pageState.scrollLeft, pageState.scrollTop])

  const markCalendarViewReady = useCallback((): void => {
    const key = viewKeyRef.current
    if (!key) return
    setViewReadyKey(key)
  }, [])

  const onCalendarViewLoaded = useCallback((): void => {
    hideLoading('.page-content')
    markCalendarViewReady()
    applyScrollPosition()
  }, [hideLoading, markCalendarViewReady, applyScrollPosition])

  const onMonthViewLoaded = useCallback((): void => {
    hideLoading('.page-content')
    markCalendarViewReady()
  }, [hideLoading, markCalendarViewReady])

  useEffect(() => {
    if (!useDsScheduler || !canMountCalendar) return
    hideLoading('.page-content')
    markCalendarViewReady()
  }, [
    useDsScheduler,
    canMountCalendar,
    hideLoading,
    markCalendarViewReady,
    activeTab,
    date,
    dsSchedulerData,
  ])

  useEffect(() => {
    dispatch(fetchAllergiesCatalog())
    dispatch(fetchMedicalConditionsCatalog())
  }, [dispatch])

  useEffect(() => {
    const loadCatalogs = async (): Promise<void> => {
      try {
        await dispatch(fetchAllProcedures(null)).unwrap()
      } catch {
        // non-blocking
      }
      try {
        await dispatch(fetchAllAppointmentTypes()).unwrap()
      } catch {
        // non-blocking
      }
    }
    void loadCatalogs()
  }, [dispatch])

  // Single appointments fetch path for date range, refresh, and clinic changes.
  useEffect(() => {
    if (!startDate || !endDate || !clinicId || !rangeKey) return

    let cancelled = false

    const runFetch = async (): Promise<void> => {
      await fetchAppointments()
      if (cancelled) return
    }

    void runFetch()
    return () => {
      cancelled = true
    }
  }, [startDate, endDate, refresh, fetchAppointments, clinicId, rangeKey])

  return (
    <>
      <Container fluid className="p-0 appt-container appt-page-root">
        <Row className="appt-container">
          <Col md="12" className="appt-content">
            {!canMountCalendar ? (
              <AppointmentSkeleton />
            ) : (
              <div
                className="appt-calendar-stage"
                style={{ position: 'relative' }}
              >
                <div
                  className="appt-calendar-stage__body"
                  style={{
                    visibility: isViewPending ? 'hidden' : 'visible',
                  }}
                >
                  <Tab.Container activeKey={activeTab}>
                    <div
                      id="calendar-header-row-top"
                      className="align-items-center justify-content-between"
                    >
                      <Col md="auto" className="d-flex align-items-start p-0">
                        <div className="d-flex flex-wrap align-items-center">
                          <Button
                            className="button-secondary today-button"
                            onClick={() =>
                              handleDateChange(moment().format('MMMM D, YYYY'))
                            }
                            size="md"
                          >
                            Today
                          </Button>
                          <ButtonGroup
                            buttons={NAV_ITEMS}
                            activeTab={activeTab}
                            onButtonClick={handleTabChange}
                            className="calendar-button-group ms-2"
                          />
                          <div className="appt-top-border-separator mx-2" />
                          <CalendarToggle
                            activeTab={activeTab}
                            startDate={startDate}
                            endDate={endDate}
                            date={date}
                            onDateChange={handleDateChange}
                          />
                          <div className="arrow-navigator">
                            <button
                              type="button"
                              className="unstyled-button"
                              aria-label="Previous day"
                              onClick={handlePrevDay}
                            >
                              <i
                                className="ti ti-chevron-left fs-1"
                                aria-hidden
                              />
                            </button>
                            <button
                              type="button"
                              className="unstyled-button"
                              aria-label="Next day"
                              onClick={handleNextDay}
                            >
                              <i
                                className="ti ti-chevron-right fs-1"
                                aria-hidden
                              />
                            </button>
                          </div>
                        </div>
                      </Col>
                      <Col md="auto" className="d-flex align-items-start p-0">
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          <Button
                            size="md"
                            className="button-secondary info-button"
                            onClick={() => setIsLegendInfoModalOpen(true)}
                          >
                            <span className="mgc_information_fill" />
                          </Button>
                          <SearchWithFilter
                            key={filterResetKey}
                            searchIcon="mgc mgc_search_2_line"
                            filterIcon="mgc mgc_settings_2_line"
                            placeholder="Search appointment"
                            searchQuery={searchQuery}
                            onChange={handleSearchChange}
                            filters={filterOptions}
                            filterLabels={{
                              Providers: 'Providers',
                              Operatories: 'Operatories',
                              Status: 'Status',
                              aiReceptionist: 'AI receptionist',
                            }}
                            onFilterChange={handleFilterChange}
                            showResetButton
                            onReset={handleResetFilters}
                          />
                          <div className="appt-top-border-separator" />
                          <Button
                            size="md"
                            className="button-secondary add-patient-button"
                            onClick={toggleAddPatientModal}
                          >
                            Add patient
                          </Button>
                          <Button
                            size="md"
                            className="button-primary set-appointment-button"
                            onClick={() => {
                              setCurrentAppointment(null)
                              setIsModalOpen(true)
                            }}
                          >
                            Set appointment
                          </Button>
                        </div>
                      </Col>
                    </div>

                    <Row id="calendar-content-row">
                      <Col md="12" className="calendar-container">
                        {useDsScheduler && dsSchedulerData ? (
                          <div className="appointment-ds-scheduler">
                            {dsSchedulerData.usingFallback ? (
                              <p className="appointment-ds-scheduler__fallback-note">
                                Showing demo appointments — live API data is
                                empty or unavailable.
                              </p>
                            ) : null}
                            <Scheduler
                              columns={dsSchedulerData.columns}
                              providers={dsSchedulerData.providers}
                              events={dsEvents}
                              onEventsChange={setDsEvents}
                              date={schedulerDate}
                              onDateChange={(next) =>
                                setDate(moment(next).format('MMMM D, YYYY'))
                              }
                              view={
                                (activeTab === 'week' || activeTab === 'month'
                                  ? activeTab
                                  : 'day') as SchedulerView
                              }
                              onViewChange={(next) => setActiveTab(next)}
                              viewMode={resolveSchedulerViewMode(
                                schedulerSettings.view_mode,
                              )}
                              blockRanges={dsSchedulerData.blockRanges}
                              showToolbar
                              showEventDetailModal={false}
                              enableInteractions
                              onSetAppointmentClick={() => {
                                setCurrentAppointment(null)
                                setIsModalOpen(true)
                              }}
                              onAddPatientClick={toggleAddPatientModal}
                              onEventClick={handleDsEventClick}
                              onSlotClick={openSetAppointmentFromSlot}
                            />
                          </div>
                        ) : (
                          <AppointmentCalendarView
                            activeTab={activeTab}
                            date={date}
                            appointmentList={calendarAppointments}
                            providerList={providerList}
                            filteredOperatories={filteredOperatories}
                            isHIPAAView={isHIPAAView}
                            schedulerSettings={calendarSchedulerSettings}
                            startDate={startDate}
                            endDate={endDate}
                            onViewLoadedDayWeek={onCalendarViewLoaded}
                            onViewLoadedMonth={onMonthViewLoaded}
                            toggleApptModal={toggleApptModal}
                            setAppointmentDetails={setAppointmentDetails}
                            onViewNote={handleOpenApptNotesOverlay}
                            aiActorId={aiActorId}
                          />
                        )}
                      </Col>
                    </Row>
                  </Tab.Container>
                </div>
                {showCalendarSkeleton ? (
                  <div className="appt-calendar-skeleton-overlay">
                    <AppointmentSkeleton />
                  </div>
                ) : null}
              </div>
            )}
          </Col>
        </Row>

        {isModalOpen && (
          <ScheduleAppointment
            isOpen={isModalOpen}
            handleModal={() => toggleApptModal(false)}
            onSave={handleOnSave}
            initialStartTime={appointmentDetails.startTime || startDate}
            initialEndTime={appointmentDetails.endTime || endDate}
            initialRoom={appointmentDetails.room || undefined}
            initialProvider={appointmentDetails.provider || undefined}
            currentAppt={currentAppointment || undefined}
            onViewNote={handleOpenApptNotesOverlay}
            {...(selectedPatient &&
              !currentAppointment && {
                preSelectedPatient: {
                  patient_id: selectedPatient.patient_id,
                  first_name: selectedPatient.first_name,
                  last_name: selectedPatient.last_name,
                  has_insurance: selectedPatient.has_insurance,
                  pat_ins: selectedPatient.pat_ins,
                } as PatientWithInsurance,
              })}
          />
        )}

        <AddPatientModal
          isOpen={showAddPatientModal}
          onRequestClose={toggleAddPatientModal}
          onSave={handleSavePatient}
          patientData={null}
        />

        <LegendInfoModal
          isOpen={isLegendInfoModalOpen}
          closeModal={() => setIsLegendInfoModalOpen(false)}
        />
      </Container>
    </>
  )
}

export default AppointmentPage
